<script setup>
import { computed, onBeforeMount, ref, defineAsyncComponent, shallowRef, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import opmStore from '@/lib/storeOwnerProjectManager';
import DataPoint from "@/components/views/inputs/DataPoint.vue";
import { useReportExport } from '@/assets/modules/composables/reportExport.js';

const route = useRoute();
const router = useRouter();

const pageData = ref();
const actions = ref();
const tables = ref();
// const documents = ref();
const response = ref();
const dialog = ref(false);
const pollingInterval = ref(null);
const pdfStatus = ref([]);
const generateButtonDisabled = ref(false);

// Dynamically import the component based on the route parameter
const entityTypeComponent = shallowRef(null);

// import { useButtonClick } from "@/assets/modules/composables/button.js";
// const { buttonClick } = useButtonClick(pageData, null);
// onBeforeMount(async() => {
//     const response = await opmStore.dispatch("getProjectStatusReport", route.params.entity_id);
//     data.value = response.data;
// })
onBeforeMount(async() => {
    entityTypeComponent.value = defineAsyncComponent(() =>
        import(`@/components/views/forms/${route.params.entity_type}Form.vue`)
    );
    let query = [];
    if(route?.query?.refetchSchemas){
        query.push({
            key: 'refetchSchemas',
            value: route.query.refetchSchemas,
        });
    }
    // const response = await opmStore.dispatch("getPageData",{
    //     entityType: route.params.entity_type,
    //     entityId: route.params.entity_id,
    //     isMainData: true,
    //     query,
    // });
    response.value = await opmStore.dispatch("getProjectStatusReport", route.params.entity_id);
    console.log("response",response.value)
    if(response.value?.success){
        pageData.value = response.value.data;
        opmStore.dispatch("setBreadcrumbs", response.value.meta.breadcrumbs);
        actions.value = response.value.meta.actions ||[];
        tables.value = response.value.meta.tables || [];
        // documents.value = response.value.meta.documents || [];
    }
})
function tableRowClick(item, row){
    router.push(row.item.href.replace(/^\/#/, '')); // Navigate to the cleaned href
}
// async function viewDocument(item, row){
//     var loadPayload = {
//         Key: row.item.value
//     };

//     await opmStore.dispatch("fetchDownload", loadPayload)
// }
async function addPdfToQueue(){
    generateButtonDisabled.value = true;
    const addPdfStatus = await opmStore.dispatch("addPdfToQueue", response.value);
    const firstPdfCheck = await opmStore.dispatch("checkPdfStatus", addPdfStatus.data.requestId);
    dialog.value = true;
    pdfStatus.value = firstPdfCheck.data.items;
    generateButtonDisabled.value = false;
    if (pollingInterval.value){
        clearInterval(pollingInterval.value);
    }
    // Start polling getPdfStatus every 2 seconds
    pollingInterval.value = setInterval(async () => {
        // const status = await opmStore.dispatch("checkPdfStatus", addPdfStatus.data.requestId);
        const pdfCheck = await opmStore.dispatch("checkPdfStatus", addPdfStatus.data.requestId);
        console.log("pdfCheck", pdfCheck);
        pdfStatus.value = pdfCheck.data.items;
        // pdfPercent.value = 100/pdfStatus.value.length;
        console.log("pdfStatus", pdfStatus.value);
        // pollingInterval.value = null;
        // // Update pdfPercent or handle status as needed
        // if (status?.data?.percent !== undefined) {
        //     pdfPercent.value = status.data.percent;
        // }
        // // Optionally stop polling when done
        console.log("pdfPercentComplete.value",pdfPercentComplete.value)
        if (pdfPercentComplete.value >= 100) {
            clearInterval(pollingInterval.value);
            pollingInterval.value = null;
            // window.open(pdfStatus.value[pdfStatus.value.length - 1].signedUrl, '_blank');
            // dialog.value = false;
        }
    }, 4000);
}
const pdfPercentComplete = computed(() => {
    return pdfStatus.value ? pdfStatus.value.filter(step => step.complete).length / pdfStatus.value.length * 100 : 0;
})
onUnmounted(() => {
    if (pollingInterval.value){
        clearInterval(pollingInterval.value);
    }
});
function openDocument(){
    window.open(pdfStatus.value[pdfStatus.value.length - 1].signedUrl, '_blank')
}

const { exportXlsx, exportCsv } = useReportExport();

// This report's tables are `disable-sort`, so display order is the API's order.
async function downloadXlsx() {
    await exportXlsx(tables.value, pageData.value.pageName);
}
function downloadCsv() {
    exportCsv(tables.value, pageData.value.pageName);
}
</script>

<template>
    <v-container fluid v-if='pageData'>
        <v-form ref="form" lazy-validation>
            <v-row>
                <v-col>
                    <h1>{{ pageData.pageName }}</h1>
                </v-col>
                <v-col class='ga-4' cols='auto'>
                    <v-menu>
                        <template #activator="{ props }">
                            <v-btn
                                class="mr-4"
                                color="primary"
                                variant="outlined"
                                prepend-icon="mdi-download"
                                append-icon="mdi-menu-down"
                                v-bind="props"
                            >
                                Export
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item prepend-icon="mdi-microsoft-excel" title="Excel (.xlsx)" @click="downloadXlsx" />
                            <v-list-item prepend-icon="mdi-file-delimited" title="CSV (.csv)" @click="downloadCsv" />
                        </v-list>
                    </v-menu>
                    <v-btn
                        v-if="pdfStatus[pdfStatus.length - 1]?.signedUrl"
                        color="primary" 
                        variant="flat"
                        @click="openDocument()"
                    >
                        Open Document
                    </v-btn>
                    <v-btn
                        v-else
                        color="primary" 
                        variant="flat"
                        :disabled="generateButtonDisabled"
                        :loading="generateButtonDisabled"
                        @click="addPdfToQueue()"
                    >
                        Generate Document
                    </v-btn>
                </v-col>
            </v-row>

            <v-row>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='owner_name'/>
                </v-col>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='owner_address'/>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>
            
            <v-row>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='project_name'/>
                </v-col>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='project_number'/>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='project_status'/>
                </v-col>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='project_estimate'/>
                </v-col>
            </v-row>
            
            <v-row>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='project_location'/>
                </v-col>
            </v-row>





            <template v-if="tables.length">
                <v-row v-for="(tableObj, index) in tables" :key="index">
                    <v-col class="mt-12">
                        <!-- Access the first key of the object dynamically -->
                        <h3>{{ Object.values(tableObj)[0].title }}</h3>
                        <v-data-table
                            :headers="Object.values(tableObj)[0].tableHeaders"
                            :items="Object.values(tableObj)[0].tableData"
                            :item-key="item => item.id || index"
                            :items-per-page="10"
                            disable-sort
                            @click:row="tableRowClick"
                        >
                        </v-data-table>
                    </v-col>
                </v-row>
            </template>


            <v-dialog 
                v-model="dialog" 
                max-width="772"
                persistent
            >
                <v-card>
                    <v-container>
                        <v-row align="start">
                            <v-col>
                                <h3 v-if="!pdfStatus[pdfStatus.length - 1].signedUrl" class="mt-2">Your document is being generated</h3>
                                <h3 v-else class="mt-2">Your document is ready</h3>
                            </v-col>
                            <v-col cols="auto">
                                <v-btn 
                                    icon 
                                    @click="dialog = false" 
                                    variant="text"
                                    :hidden="pdfPercentComplete < 100"
                                >
                                    <v-icon>mdi-close</v-icon>
                                </v-btn>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-progress-linear
                                height="10"
                                v-model="pdfPercentComplete"
                                :striped="!pdfStatus[pdfStatus.length - 1].signedUrl"
                                color="green"
                                ></v-progress-linear>
                                <v-list v-if="pdfStatus && pdfStatus.length" class="mt-4">
                                    <v-list-item
                                        v-for="(step, idx) in pdfStatus"
                                        :key="idx"
                                        class="pl-0"
                                    >
                                        <v-list-item-title>
                                            <v-icon
                                                v-if="step.complete"
                                                color="success"
                                                class="mr-2"
                                            >mdi-check-circle</v-icon>
                                            <v-icon
                                                v-else
                                                color="grey"
                                                class="mr-2"
                                            >mdi-checkbox-blank-circle-outline</v-icon>
                                            {{ step.step }}
                                        </v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-col>
                        </v-row>
                        <v-row justify="end">
                            <v-col cols="auto">
                                <v-btn
                                    text="Open Document"
                                    color="primary" 
                                    variant="flat"
                                    :disabled="!pdfStatus[pdfStatus.length - 1].signedUrl"
                                    @click="openDocument"
                                ></v-btn>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </v-dialog>



        </v-form>
    </v-container>
</template>